import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  ShieldAlert,
  Download,
  RefreshCw,
  X as XIcon,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";

/**
 * Bot Analytics — per-crawler cards + inline drill-down.
 *
 * UX principles
 * -------------
 * • Exactly 9 priority crawler cards (Googlebot · Bingbot · AdsBot-Google ·
 *   OAI-SearchBot · GPTBot · PerplexityBot · Meta-ExternalAgent · Applebot ·
 *   ClaudeBot). Each card = one bot the operator cares about for GEO /
 *   search visibility. HeadlessChrome is intentionally NOT a card.
 * • Each card is clickable. Clicking opens an inline drill-down below
 *   that shows the last 3 days of that bot's activity — timestamp, path,
 *   method, status, response time, verified badge, redacted IP.
 * • CSV export always ships the LAST 30 DAYS of crawler activity across
 *   all 9 families. Downloaded via authenticated blob fetch (the previous
 *   `window.open` approach failed with "Missing bearer token" because the
 *   browser cannot attach the Authorization header on a plain navigation).
 * • Suspicious Activity panel still sits at the top so the operator sees
 *   abusive scrapers before legitimate crawler cards.
 */

// ---- Priority bot catalogue -------------------------------------------------
// Monogram tile styling only — the family key MUST match the backend
// `PRIORITY_FAMILIES` values exactly.
const BOTS = [
  { family: "Googlebot",          label: "Googlebot",           mono: "G",   bg: "#4285F4", fg: "#FFFFFF", blurb: "Google Search index" },
  { family: "Bingbot",            label: "Bingbot",             mono: "b",   bg: "#008373", fg: "#FFFFFF", blurb: "Microsoft Bing / Copilot" },
  { family: "AdsBot-Google",      label: "AdsBot-Google",       mono: "Ad",  bg: "#34A853", fg: "#FFFFFF", blurb: "Google Ads landing-page quality" },
  { family: "OAI-SearchBot",      label: "OAI-SearchBot",       mono: "o·s", bg: "#10A37F", fg: "#FFFFFF", blurb: "ChatGPT Search crawler" },
  { family: "GPTBot",             label: "GPTBot",              mono: "GPT", bg: "#111827", fg: "#FFFFFF", blurb: "OpenAI training crawler" },
  { family: "PerplexityBot",      label: "PerplexityBot",       mono: "P",   bg: "#20808D", fg: "#FFFFFF", blurb: "Perplexity.ai answer engine" },
  { family: "Meta-ExternalAgent", label: "Meta-ExternalAgent",  mono: "M",   bg: "#1877F2", fg: "#FFFFFF", blurb: "Meta (Facebook / Instagram) AI" },
  { family: "Applebot",           label: "Applebot",            mono: "",    bg: "#1D1D1F", fg: "#FFFFFF", blurb: "Apple Intelligence / Siri" },
  { family: "ClaudeBot",          label: "Claude / Anthropic",  mono: "C",   bg: "#CC785C", fg: "#FFFFFF", blurb: "Anthropic Claude crawler" },
];

const fmt = (n) => (n || 0).toLocaleString();
const fmtLastSeen = (iso) => {
  if (!iso) return "No hits yet";
  const d = new Date(iso);
  const diffMin = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 24 * 60) return `${Math.round(diffMin / 60)}h ago`;
  return d.toLocaleDateString();
};

export default function BotTrafficInsights({ range = "last_week", rangeLabel = "Last 7 days" }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [susp, setSusp] = useState(null);
  const [newBots, setNewBots] = useState([]);
  const [downloading, setDownloading] = useState(false);

  // ---- fetch per-family counts for the range --------------------------------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get("/stats/crawler-families-stats", { params: { range } }),
      api.get("/stats/suspicious-activity"),
      api.get("/stats/recent-new-bots", { params: { limit: 8 } }).catch(() => ({ data: { items: [] } })),
    ])
      .then(([a, b, c]) => {
        if (cancelled) return;
        setStats(a.data);
        setSusp(b.data);
        setNewBots(c.data?.items || []);
      })
      .catch(() => { if (!cancelled) { setStats(null); setSusp(null); setNewBots([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range]);

  const countByFamily = useMemo(() => {
    const m = {};
    (stats?.families || []).forEach((f) => { m[f.family] = f; });
    return m;
  }, [stats]);

  // ---- Evidence-based health (single source: crawler_requests) ------------
  const [bh, setBh] = useState(null);
  const [panel, setPanel] = useState(null);
  useEffect(() => {
    let live = true;
    api.get("/stats/bot-health", { params: { range } })
      .then((r) => { if (live) setBh(r.data); })
      .catch(() => { if (live) setBh(null); });
    return () => { live = false; };
  }, [range]);

  // ---- Health roll-up (verified / spoofed / pending across all 9) -----------
  const health = useMemo(() => {
    const fams = stats?.families || [];
    const total = fams.reduce((a, f) => a + (f.count || 0), 0);
    const verified = fams.reduce((a, f) => a + (f.verified || 0), 0);
    const spoofed = fams.reduce((a, f) => a + (f.spoofed || 0), 0);
    const pending = Math.max(0, total - verified - spoofed);
    const active = fams.filter((f) => (f.count || 0) > 0).length;
    // GEO health score: green when spoof ratio is low AND at least half the
    // priority bots have hit us; amber when spoof ratio is moderate; red
    // otherwise. 0 traffic falls back to "idle" (neutral tone).
    let tone = "neutral";
    let label = "Idle";
    if (total > 0) {
      const spoofPct = (spoofed / total) * 100;
      if (spoofPct < 10 && active >= 5) { tone = "green"; label = "Healthy"; }
      else if (spoofPct < 30) { tone = "amber"; label = "Watch"; }
      else { tone = "red"; label = "At risk"; }
    }
    return { total, verified, spoofed, pending, active, tone, label };
  }, [stats]);

  // ---- CSV export (authenticated blob — fixes "Missing bearer token") -------
  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/stats/crawler-log.csv", {
        params: { range: "last_month" },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `propertic-crawler-log-30d-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      // Surface a minimal error — the operator will usually retry
      // after a stale session re-auth. Avoid noisy modal here.
      console.error("CSV download failed", e);
      alert("CSV download failed. Please refresh and try again.");
    } finally {
      setDownloading(false);
    }
  };

  const incidents = susp?.incidents || [];
  const totalRange = stats?.total || 0;

  return (
    <section className="mb-8" data-testid="bot-insights-section">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 className="font-editorial text-lg sm:text-xl text-propertic-navy">Bot analytics</h2>
          <span className="text-[10px] sm:text-[11px] text-propertic-muted font-mono-brand tracking-[0.12em] uppercase">
            {rangeLabel} · {fmt(totalRange)} priority-crawler requests
          </span>
        </div>
        <button
          onClick={downloadCsv}
          disabled={downloading}
          data-testid="crawler-csv-export"
          className="h-8 px-3 rounded-lg bg-propertic-navy text-white text-[10px] font-mono-brand uppercase tracking-wide inline-flex items-center gap-1.5 disabled:opacity-60 hover:bg-propertic-navy-2 transition-colors"
          title="Download last 30 days of crawler activity"
        >
          {downloading
            ? <RefreshCw className="w-3 h-3 animate-spin" />
            : <Download className="w-3 h-3" />}
          Export CSV · 30D
        </button>
      </div>

      {/* ---------- Suspicious activity (always on top when present) -------- */}
      {incidents.length > 0 && (
        <div
          className="mb-5 bg-rose-50 border border-rose-200 rounded-2xl p-4"
          data-testid="suspicious-activity"
        >
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="w-4 h-4 text-rose-600" />
            <h3 className="font-editorial text-base text-rose-900">
              Suspicious activity · {incidents.length} incident{incidents.length === 1 ? "" : "s"}
            </h3>
          </div>
          <ul className="space-y-2">
            {incidents.map((i, idx) => (
              <li key={idx} className="bg-white rounded-lg p-3 text-[12px] border border-rose-100" data-testid={`incident-${idx}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono-brand text-rose-700 text-[10px] uppercase tracking-wide">{i.label}</span>
                  <span className="font-mono-brand text-[10px] text-propertic-muted">
                    {new Date(i.first_seen).toLocaleTimeString()} → {new Date(i.last_seen).toLocaleTimeString()}
                  </span>
                </div>
                <p className="mt-1 text-propertic-ink">
                  <strong className="tabular">{i.count}</strong> requests from <span className="font-mono-brand">{i.ip_display}</span>
                  {i.families?.length ? <> · families: <span className="font-mono-brand">{i.families.join(", ")}</span></> : null}
                </p>
                <p className="text-[11px] text-propertic-muted mt-1 truncate">
                  Paths: {(i.paths_sample || []).slice(0, 3).join(" · ")}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ---------- Health strip (GEO signal at a glance) ------------------ */}
      {!loading && bh && (
        <>
        <p className="mb-2 text-[11px] text-propertic-muted" data-testid="bot-coverage-note">{bh.coverage_note}{bh.partial ? " · current period is partial." : ""}</p>
        <div
          className="mb-5 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3"
          data-testid="bot-health-strip"
        >
          <HealthPill
            label="Crawler API health"
            value={bh.geo.status}
            tone={{ Healthy: "green", Watch: "amber", "At Risk": "red" }[bh.geo.status] || "neutral"}
            caption={bh.geo.explanation}
            prominent
            testid="health-score"
            onClick={() => setPanel("geo")}
          />
          <HealthPill
            label="Verified requests"
            value={fmt(bh.states.verified)}
            tone="green"
            caption="provider-verified (requests, not IPs)"
            testid="health-verified"
            onClick={() => setPanel("verified")}
          />
          <HealthPill
            label="Confirmed spoofed"
            value={fmt(bh.states.spoofed)}
            tone={bh.states.spoofed > 0 ? "red" : "neutral"}
            caption={`${fmt(bh.states.unverified + bh.states.unavailable)} unverified / unavailable (not spoofed)`}
            testid="health-spoofed"
            onClick={() => setPanel("spoofed")}
          />
          <HealthPill
            label="Pending verification"
            value={fmt(bh.states.pending)}
            tone={bh.states.pending > 0 ? "amber" : "neutral"}
            caption="queued checks (normally seconds)"
            testid="health-pending"
            onClick={() => setPanel("pending")}
          />
          <HealthPill
            label="New bot labels"
            value={fmt(bh.new_families)}
            tone={bh.new_families > 0 ? "gold" : "neutral"}
            caption={bh.latest_new_family ? `latest: ${bh.latest_new_family}` : "none in range"}
            testid="health-new-families"
            onClick={() => setPanel("new_families")}
          />
        </div>
        </>
      )}
      {panel && <InvestigationPanel panel={panel} range={range} onClose={() => setPanel(null)} />}

      {/* ---------- 9 priority crawler cards (loading or ready) ------------- */}
      {loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-3 mb-6" data-testid="bot-cards-loading">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="h-28 rounded-2xl bg-propertic-cream/60 animate-pulse" />
          ))}
        </div>
      )}

      {!loading && (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5 gap-3 mb-6"
          data-testid="bot-card-grid"
        >
          {BOTS.map((b) => {
            const row = countByFamily[b.family] || { count: 0, verified: 0, spoofed: 0, share_pct: 0, last_seen: null };
            const active = selected === b.family;
            return (
              <button
                key={b.family}
                type="button"
                onClick={() => setSelected(active ? null : b.family)}
                data-testid={`bot-card-${b.family}`}
                className={`text-left rounded-2xl border hairline bg-white p-4 flex flex-col gap-3 transition-all hover:border-propertic-gold hover:shadow-sm ${active ? "ring-2 ring-propertic-gold border-propertic-gold shadow-sm" : ""}`}
                aria-pressed={active}
              >
                <div className="flex items-center justify-between gap-2">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center font-mono-brand text-[13px] tracking-tight flex-shrink-0"
                    style={{ backgroundColor: b.bg, color: b.fg }}
                    aria-hidden
                  >
                    {b.mono ? b.mono : (
                      // Apple glyph fallback — the pure-logo card has no monogram
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M17.5 12.7c0-2.9 2.4-4.3 2.5-4.4-1.4-2-3.5-2.3-4.3-2.3-1.8-.2-3.5 1.1-4.4 1.1-.9 0-2.3-1.1-3.8-1-2 0-3.8 1.1-4.8 2.9-2.1 3.6-.5 8.9 1.5 11.8 1 1.4 2.2 3 3.7 2.9 1.5-.1 2-1 3.8-1s2.3 1 3.8 1c1.6 0 2.6-1.4 3.5-2.9 1.1-1.6 1.6-3.3 1.6-3.4-.1 0-3.1-1.2-3.1-4.7zM14.6 4.7c.8-1 1.3-2.4 1.2-3.7-1.2 0-2.6.8-3.4 1.8-.7.9-1.4 2.3-1.2 3.6 1.4.1 2.7-.7 3.4-1.7z" />
                      </svg>
                    )}
                  </div>
                  <span className="font-mono-brand text-[10px] text-propertic-muted tabular">
                    {row.share_pct || 0}%
                  </span>
                </div>

                <div>
                  <p className="font-mono-brand text-[10px] tracking-[0.14em] uppercase text-propertic-muted truncate">
                    {b.label}
                  </p>
                  <p className="font-editorial text-2xl text-propertic-navy tabular-nums leading-tight">
                    {fmt(row.count)}
                  </p>
                  <p className="text-[10px] text-propertic-muted truncate" title={b.blurb}>
                    {b.blurb}
                  </p>
                </div>

                <div className="flex items-center gap-1.5 flex-wrap text-[9px] font-mono-brand uppercase tracking-wide">
                  {row.verified > 0 && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                      <CheckCircle2 className="w-2.5 h-2.5" /> {row.verified}
                    </span>
                  )}
                  {row.spoofed > 0 && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                      <AlertTriangle className="w-2.5 h-2.5" /> {row.spoofed}
                    </span>
                  )}
                  <span className="ml-auto text-propertic-muted tabular-nums">
                    {fmtLastSeen(row.last_seen)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* ---------- Inline drill-down (last 3 days of selected bot) --------- */}
      {selected && (
        <BotDrillDown
          family={selected}
          label={BOTS.find((b) => b.family === selected)?.label || selected}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
}

// =============================================================================
// Drill-down panel — last 3 days of a single family's requests.
// =============================================================================
function BotDrillDown({ family, label, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { range: "last_3d", family, limit: 300 };
    if (verified) params.verified = verified;
    api.get("/stats/crawler-log", { params })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [family, verified]);

  const items = data?.items || [];

  return (
    <div
      className="bg-white rounded-2xl border hairline overflow-hidden mb-4"
      data-testid={`bot-drilldown-${family}`}
    >
      <div className="px-5 py-3 border-b hairline flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <h3 className="font-editorial text-base text-propertic-navy">{label}</h3>
          <span className="text-[10px] font-mono-brand text-propertic-muted uppercase tracking-wide">
            {data ? `${data.returned} of ${data.total} · ${data.range_label}` : "Loading…"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={verified}
            onChange={(e) => setVerified(e.target.value)}
            className="h-7 px-2 rounded-lg text-[11px] bg-white border hairline"
            data-testid="drilldown-verify-filter"
          >
            <option value="">All</option>
            <option value="verified">Verified only</option>
            <option value="unverified">Spoofed only</option>
            <option value="pending">Pending verify</option>
          </select>
          <button
            type="button"
            onClick={onClose}
            data-testid={`bot-drilldown-close-${family}`}
            className="h-7 w-7 rounded-lg border hairline inline-flex items-center justify-center text-propertic-muted hover:text-propertic-navy hover:border-propertic-gold"
            aria-label="Close drill-down"
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="text-propertic-muted bg-propertic-cream/40">
            <tr>
              <th className="text-start px-4 py-2 font-mono-brand text-[9px] uppercase tracking-wider whitespace-nowrap">Time</th>
              <th className="text-start px-4 py-2 font-mono-brand text-[9px] uppercase tracking-wider">Path</th>
              <th className="text-end   px-4 py-2 font-mono-brand text-[9px] uppercase tracking-wider">HTTP</th>
              <th className="text-end   px-4 py-2 font-mono-brand text-[9px] uppercase tracking-wider">Response</th>
              <th className="text-start px-4 py-2 font-mono-brand text-[9px] uppercase tracking-wider">Verified</th>
              <th className="text-start px-4 py-2 font-mono-brand text-[9px] uppercase tracking-wider">Type</th>
            </tr>
          </thead>
          <tbody className="divide-y hairline">
            {loading && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-[11px] text-propertic-muted">
                <RefreshCw className="w-3 h-3 animate-spin inline-block mr-2 align-[-2px]" /> Loading last 3 days…
              </td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-[11px] text-propertic-muted">
                No hits from {label} in the last 3 days.
              </td></tr>
            )}
            {!loading && items.map((r, i) => (
              <tr key={i} className="hover:bg-propertic-cream/30" data-testid={`drilldown-row-${i}`}>
                <td className="px-4 py-2 font-mono-brand text-[10px] text-propertic-muted whitespace-nowrap">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                </td>
                <td
                  className="px-4 py-2 truncate max-w-[320px] font-mono-brand text-[11px]"
                  title={(r.path || "") + (r.query ? "?" + r.query : "")}
                >
                  {r.path || "—"}
                </td>
                <td className={`px-4 py-2 text-right font-mono-brand tabular ${r.status >= 500 ? "text-rose-600" : r.status >= 400 ? "text-amber-600" : r.status ? "text-propertic-navy" : "text-propertic-muted"}`}>
                  {r.status ?? "—"}
                </td>
                <td className="px-4 py-2 text-right font-mono-brand text-[10px] text-propertic-muted tabular whitespace-nowrap">
                  {r.response_ms != null ? `${Number(r.response_ms).toFixed(0)} ms` : "—"}
                </td>
                <td className="px-4 py-2">
                  {r.verified === true && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[9px] font-mono-brand">Verified</span>}
                  {r.verified === false && <span className="px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded text-[9px] font-mono-brand">Unverified</span>}
                  {r.verified == null && <span className="text-[9px] text-propertic-muted font-mono-brand">Pending</span>}
                </td>
                <td className="px-4 py-2">
                  <span className="inline-flex items-center px-1.5 py-0.5 bg-propertic-cream text-propertic-ink rounded text-[9px] font-mono-brand">
                    {r.request_type || "Other"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Legacy sibling: AdminStats previously imported `CrawlerActivitySection`
// from a sibling file. That section has been folded into this component
// per Rani's feedback (the "Every logged request" HeadlessChrome-dominated
// panel is replaced with per-bot drill-downs). The sibling file still
// exists as dead code but is no longer mounted anywhere.

// =============================================================================
// HealthPill — compact status card used in the "GEO health" strip.
// =============================================================================
const _TONES = {
  neutral: { bg: "bg-white",        chip: "bg-propertic-cream text-propertic-muted" },
  green:   { bg: "bg-emerald-50/60", chip: "bg-emerald-100 text-emerald-700" },
  amber:   { bg: "bg-amber-50/60",   chip: "bg-amber-100 text-amber-800" },
  red:     { bg: "bg-rose-50/60",    chip: "bg-rose-100 text-rose-700" },
  gold:    { bg: "bg-propertic-sand/50", chip: "bg-propertic-gold/15 text-propertic-gold" },
};

function HealthPill({ label, value, caption, tone = "neutral", prominent = false, testid, onClick }) {
  const t = _TONES[tone] || _TONES.neutral;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${label}: ${value}. Open investigation panel`}
      className={`text-start rounded-2xl border hairline ${t.bg} p-3 flex flex-col gap-1 hover:border-propertic-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-propertic-gold transition-colors ${prominent ? "sm:col-span-1 lg:col-span-1" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="font-mono-brand text-[9px] tracking-[0.14em] uppercase text-propertic-muted truncate">{label}</p>
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-mono-brand uppercase tracking-wide ${t.chip}`}>
          {tone === "neutral" ? "idle" : tone === "green" ? "ok" : tone === "amber" ? "watch" : tone === "red" ? "alert" : "new"}
        </span>
      </div>
      <p className={`font-editorial ${prominent ? "text-xl" : "text-2xl"} text-propertic-navy tabular-nums leading-tight truncate`} title={String(value)}>
        {value}
      </p>
      {caption && <p className="text-[10px] text-propertic-muted truncate" title={caption}>{caption}</p>}
    </button>
  );
}

const PANEL_TITLES = {
  geo: "Crawler API health — all monitored crawler requests",
  verified: "Verified requests",
  spoofed: "Confirmed spoofed requests",
  pending: "Pending verification queue",
  new_families: "New bot labels",
};

function InvestigationPanel({ panel, range, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    api.get("/stats/bot-investigate", { params: { panel, range } })
      .then((r) => { if (live) setData(r.data); })
      .catch(() => { if (live) setData({ items: [], error: true }); });
    return () => { live = false; };
  }, [panel, range]);
  const items = data?.items || [];
  const cols = panel === "new_families"
    ? ["bot_name", "family", "classification", "first_seen", "total_count"]
    : ["created_at", "family", "state", "verification_method", "verify_evidence", "ip", "method", "path", "category", "status", "response_ms", ...(panel === "pending" ? ["pending_seconds"] : [])];
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-2 sm:p-6" onClick={onClose} data-testid="bot-investigation-panel">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 p-4 border-b hairline">
          <div className="min-w-0">
            <p className="font-editorial text-lg text-propertic-navy">{PANEL_TITLES[panel]}</p>
            <p className="text-[11px] text-propertic-muted">{data?.range} · {items.length} records{data?.note ? ` · ${data.note}` : ""}</p>
            {data?.coverage_note && <p className="text-[10px] text-propertic-muted">{data.coverage_note}</p>}
          </div>
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-full border hairline text-xs" data-testid="bot-investigation-close">Close</button>
        </div>
        <div className="overflow-auto">
          {!data ? <p className="p-4 text-sm">Loading…</p> : items.length === 0 ? (
            <p className="p-4 text-sm text-propertic-muted" data-testid="bot-investigation-empty">{data.error ? "Could not load records." : "No records in this range."}</p>
          ) : (
            <table className="w-full text-[11px]" data-testid="bot-investigation-table">
              <thead className="sticky top-0 bg-propertic-cream/80"><tr>{cols.map((c) => <th key={c} className="text-start px-3 py-2 font-mono-brand text-[9px] uppercase">{c.replace(/_/g, " ")}</th>)}</tr></thead>
              <tbody className="divide-y divide-propertic-navy/5">
                {items.map((r, i) => (
                  <tr key={i}>{cols.map((c) => <td key={c} className="px-3 py-1.5 align-top break-all">{r[c] == null || r[c] === "" ? <span className="text-propertic-muted">— not recorded</span> : String(r[c])}</td>)}</tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
